import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getErrorMessage } from 'utils';

import NotificationService from 'modules/notifications/notificationService';

import { DSP_LABELS } from '../../utils/constants';
import { getCurrentAdvertiserId, getDsp } from '../organization/organizationSlice';
import { dspApi } from './dspApi';
import {
  DspInsertionOrder,
  DspInsertionOrderUpdate,
  DspLineItem,
  DspLineItemUpdate,
  DspStatusUpdate,
} from './dspApiTypes';
import {
  getInsertionOrdersFailure,
  getInsertionOrdersStart,
  getInsertionOrdersSuccess,
  updateInsertionOrdersFailure,
  updateInsertionOrdersStart,
  updateInsertionOrdersSuccess,
  updateLineItemsFailure,
  updateLineItemsStart,
  updateLineItemsSuccess,
  updateStatusFailure,
  updateStatusStart,
  updateStatusSuccess,
  refreshDspMetadataFailure,
  refreshDspMetadataStart,
  refreshDspMetadataSuccess,
} from './dspSlice';

function* getInsertionOrdersFlow(action: PayloadAction<string>) {
  try {
    const dsp: string = yield select(getDsp);
    const response: DspInsertionOrder[] = yield call(
      dspApi.getDspInsertionOrders,
      action.payload,
      dsp,
    );

    yield put(getInsertionOrdersSuccess(response));
  } catch (error) {
    yield put(getInsertionOrdersFailure(getErrorMessage(error)));
  }
}

function* updateInsertionOrdersFlow(action: PayloadAction<DspInsertionOrderUpdate[]>) {
  try {
    const dsp: string = yield select(getDsp);
    const currentAdvertiserId: string = yield select(getCurrentAdvertiserId);
    const updatedIOs: DspInsertionOrderUpdate[] = yield call(
      dspApi.updateDspInsertionOrders,
      action.payload,
      dsp,
      currentAdvertiserId,
    );

    yield put(updateInsertionOrdersSuccess(updatedIOs));
  } catch (error) {
    yield put(updateInsertionOrdersFailure(getErrorMessage(error)));
  }
}

function* updateLineItemsFlow(action: PayloadAction<DspLineItemUpdate[]>) {
  try {
    const dsp: string = yield select(getDsp);
    const currentAdvertiserId: string = yield select(getCurrentAdvertiserId);
    const updatedLIs: DspLineItem[] = yield call(
      dspApi.updateDspLineItems,
      action.payload,
      dsp,
      currentAdvertiserId,
    );

    yield put(updateLineItemsSuccess(updatedLIs));

    const response: DspInsertionOrder[] = yield call(
      dspApi.getDspInsertionOrders,
      currentAdvertiserId,
      dsp,
    );

    yield put(getInsertionOrdersSuccess(response));
  } catch (error) {
    yield put(updateLineItemsFailure(getErrorMessage(error)));
  }
}

function* updateStatusFlow(action: PayloadAction<DspStatusUpdate>) {
  try {
    const dsp: string = yield select(getDsp);
    const updatedStatusObj: DspStatusUpdate = yield call(dspApi.updateStatus, action.payload, dsp);

    yield put(updateStatusSuccess(updatedStatusObj));
  } catch (error) {
    yield put(updateStatusFailure(getErrorMessage(error)));
  }
}

function* refreshDspMetadataFlow(action: PayloadAction<{ dsp: string; entityType: string }>) {
  const { dsp, entityType } = action.payload;
  // Vendor casing ('FreeWheel'), so a lookup rather than title-casing.
  const dspLabel = DSP_LABELS[dsp] ?? dsp;
  // replace(/_/g) not replace('_'): 'creative_line_items' has two underscores
  // and String.replace with a string argument only replaces the first.
  const displayName = entityType.toLowerCase().replace(/_/g, ' ');

  try {
    const response: string = yield call(dspApi.refreshDspMetadata, dsp, entityType);

    NotificationService.success(`${dspLabel} ${displayName} sync started`);
    yield put(refreshDspMetadataSuccess(response));
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    const message = (error as { response?: { data?: { message?: string } } })?.response?.data
      ?.message;

    if (status === 409) {
      NotificationService.warn(message || `${dspLabel} metadata refresh is already in progress`);
    } else {
      NotificationService.error(getErrorMessage(error));
    }

    yield put(refreshDspMetadataFailure(getErrorMessage(error)));
  }
}

export function* dspSagas() {
  yield all([
    takeLatest(getInsertionOrdersStart.type, getInsertionOrdersFlow),
    takeLatest(updateInsertionOrdersStart.type, updateInsertionOrdersFlow),
    takeLatest(updateLineItemsStart.type, updateLineItemsFlow),
    takeLatest(updateStatusStart.type, updateStatusFlow),
    takeLatest(refreshDspMetadataStart.type, refreshDspMetadataFlow),
  ]);
}
